// src/app/features/investment/config/investment-form.config.ts
// Investment Form Definition - Enhanced with Separate Logo & Gallery Upload
// ✅ FIXED: Proper type handling and API compatibility

import { FormWizardConfig, FormSection } from '@shared/components/form/form-wizard/models/form-wizard.models';
import { InvestmentDetailsWithCountsDto } from '@core/api';
import type { CreateInvestmentRequest } from '@core/api/contracts';
import * as API_M from '@core/api/models';
import { parseDateOnly } from '@shared/utils/date-only.util';

export const INVESTMENT_FORM_SECTION_IDS = {
  NAME: 'investment-name',
  COMPANY: 'investment-company',
  INTERNAL_NOTE: 'investment-internal-note',
  LOCATION: 'investment-location',
  SALE_START: 'investment-sale-start',
  DESCRIPTION: 'investment-description',
  DOCUMENTS: 'investment-documents',
  BUILDINGS: 'investment-buildings',
  CREATE_BASIC: 'investment-create-basic',
  CREATE_BUILDINGS: 'investment-create-buildings',
  CREATE_ADDITIONAL: 'investment-create-additional'
} as const;

/**
 * Enhanced Investment Form Configuration
 *
 * UX Principles Applied:
 * 1. Required fields (Section 1) - Always visible, cannot collapse
 * 2. Optional fields (Sections 2-3) - Collapsible, collapsed by default
 * 3. Clear visual hierarchy between required and optional
 * 4. Progressive disclosure - reveal complexity as needed
 * 5. Separate logo (single) and gallery (multiple) uploads
 */

/**
 * Interface for Investment Form including UI-only helper fields
 */
export interface InvestmentForm extends Omit<Partial<CreateInvestmentRequest>, 'buildings' | 'saleStartDate'> {
  saleStartDate?: Date | string | null;
  description?: string;
  buildingPermitNumber?: string;
  phase?: string;
  prospectUrl?: string;
  address?: API_M.InvestmentAddressRequest;
  locationDisplay?: string;
  openLocationModal?: string | null;
  buildingsToCreate?: API_M.CreateBuildingRequest[];
  buildings?: API_M.BuildingDto[];
}

/**
 * Main investment form configuration factory
 */
export function getInvestmentFormConfig(
  mode: 'create' | 'edit' | 'view' = 'create',
  investmentId?: number,
  investmentData?: InvestmentDetailsWithCountsDto
): FormWizardConfig<InvestmentForm> {
  const isCreateMode = mode === 'create';

  // Dashboard sections - used for edit/view modes
  const getDashboardSections = (): FormSection[] => [
    {
      id: INVESTMENT_FORM_SECTION_IDS.NAME,
      title: 'Nazwa inwestycji',
      description: 'Zarządzaj nazwą inwestycji',
      icon: 'pi-tag',
      showInView: true,
      viewWidget: 'details',
      fields: [
        {
          key: 'title',
          label: 'Nazwa inwestycji',
          type: 'text',
          required: true,
          colSpan: 12,
          placeholder: 'np. Apartamenty Nad Wisłą',
          showInView: true,
          viewOrder: 1,
          viewType: 'identity',
          validations: [
            { type: 'required', message: 'Nazwa inwestycji jest wymagana' },
            { type: 'minLength', value: 3, message: 'Nazwa musi mieć co najmniej 3 znaki' }
          ]
        }
      ]
    },
    {
      id: INVESTMENT_FORM_SECTION_IDS.COMPANY,
      title: 'Firma',
      description: 'Firma przypisana do inwestycji',
      icon: 'pi-briefcase',
      showInView: true,
      viewWidget: 'secondary',
      customEdit: true,
      emptyStateTitle: 'Przypisz firmę do inwestycji',
      emptyEditIcon: 'pi-plus',
      fields: [
        {
          key: 'company.companyName',
          label: 'Firma',
          type: 'text',
          required: false,
          colSpan: 12,
          showInView: true,
          viewOrder: 1,
          viewType: 'list-item',
          customEdit: false,
        },
      ],
    },
    {
      id: INVESTMENT_FORM_SECTION_IDS.INTERNAL_NOTE,
      title: 'Notatka wewnętrzna',
      description: 'Notatki widoczne tylko dla załogi',
      icon: 'pi pi-lock',
      showInView: true,
      viewWidget: 'sidebar-left', // Restored to left sidebar (stacked in right)
      fields: [
        {
          key: 'internalNote',
          label: 'Notatka wewnętrzna',
          type: 'textarea',
          required: false,
          colSpan: 12,
          rows: 3,
          showInView: true,
          viewOrder: 4,
          viewType: 'list-item',
          viewLabelOverride: 'Notatki wewnętrzne'
        }
      ]
    },
    {
      id: INVESTMENT_FORM_SECTION_IDS.LOCATION,
      title: 'Lokalizacja',
      description: 'Adres inwestycji',
      icon: 'pi-map-marker',
      showInView: true,
      viewWidget: 'secondary',
      customEdit: true,
      emptyStateTitle: 'Dodaj adres inwestycji',
      emptyEditIcon: 'pi-map-marker',
      fields: [
        {
          key: 'address.city',
          label: 'Miasto',
          type: 'text',
          required: true,
          colSpan: 6,
          showInView: true,
          viewOrder: 1,
          viewType: 'list-item'
        },
        {
          key: 'address.street',
          label: 'Ulica',
          type: 'text',
          required: true,
          colSpan: 6,
          showInView: true,
          viewOrder: 2,
          viewType: 'list-item'
        },
        {
          key: 'address.postalCode',
          label: 'Kod pocztowy',
          type: 'text',
          required: true,
          colSpan: 4,
          showInView: true,
          viewOrder: 3,
          viewType: 'list-item'
        }
      ]
    },
    {
      id: INVESTMENT_FORM_SECTION_IDS.SALE_START,
      title: 'Rozpoczęcie sprzedaży',
      description: 'Data rozpoczęcia sprzedaży',
      icon: 'pi-calendar',
      showInView: true,
      viewWidget: 'secondary',
      customEdit: false,
      emptyStateTitle: 'Brak daty rozpoczęcia sprzedaży',
      warning: {
        message: 'Data rozpoczęcia sprzedaży jest wymagana, aby inwestycja mogła być poprawnie zapisywana do GOV.',
        condition: (data: any) => !data.saleStartDate && !data.gov?.saleStartDate
      },
      fields: [
        {
          key: 'saleStartDate',
          label: '',
          type: 'date',
          required: false,
          colSpan: 12,
          min: new Date().getTime(),
          showInView: true,
          viewOrder: 3,
          viewType: 'date'
        }
      ]
    },
    {
      id: INVESTMENT_FORM_SECTION_IDS.DESCRIPTION,
      title: 'Opis i udogodnienia',
      description: 'Szczegółowy opis i cechy inwestycji',
      icon: 'pi-align-left',
      showInView: true,
      viewWidget: 'details',
      fields: [
        {
          key: 'description',
          label: 'Opis i udogodnienia',
          type: 'descriptionEnhanced',
          required: false,
          colSpan: 12,
          hideOnEdit: false,
          showInView: true,
          viewOrder: 1,
          viewType: 'description-enhanced'
        }
      ]
    },
    {
      id: INVESTMENT_FORM_SECTION_IDS.DOCUMENTS,
      title: 'Dokumenty',
      description: 'Pliki i linki',
      icon: 'pi pi-folder-open',
      showInView: true,
      viewWidget: 'secondary',
      emptyStateTitle: 'Prospekt inwestycji',
      emptyEditIcon: 'pi-cog',
      fields: [
        {
          key: 'prospectUrl',
          label: 'Link do prospektu',
          type: 'url',
          required: false,
          colSpan: 12,
          showInView: true,
          viewOrder: 5,
          viewType: 'link',
          viewLabelOverride: 'Prospekt'
        }
      ]
    },
    {
      id: INVESTMENT_FORM_SECTION_IDS.BUILDINGS,
      title: 'Budynki',
      description: 'Budynki wchodzące w skład inwestycji',
      icon: 'pi pi-building',
      showInView: true,
      viewWidget: 'secondary',
      emptyStateTitle: 'Brak budynków',
      fields: [
        {
          key: 'buildings',
          label: '',
          type: 'kv-list',
          required: false,
          colSpan: 12,
          showInView: true,
          viewOrder: 1,
          viewType: 'kv-list'
        }
      ]
    }
    // NOTE: Galeria mediów jest teraz obsługiwana przez MediaGallerySectionComponent
    // zamiast tutaj jako sekcja formularza
  ];

  // Creation sections aligned with backend create contract.
  const getCreationSections = (): FormSection[] => [
    {
      id: INVESTMENT_FORM_SECTION_IDS.CREATE_BASIC,
      title: 'Podstawowe informacje',
      description: 'Wymagane dane inwestycji',
      icon: 'pi-info-circle',
      collapsible: false,
      fields: [
        {
          key: 'title',
          label: 'Nazwa inwestycji',
          type: 'text',
          required: true,
          colSpan: 6,
          placeholder: 'np. Apartamenty Nad Wisłą',
          validations: [
            { type: 'required', message: 'Nazwa inwestycji jest wymagana' },
            { type: 'minLength', value: 3, message: 'Nazwa musi mieć co najmniej 3 znaki' }
          ]
        },
        {
          key: 'saleStartDate',
          label: 'Data rozpoczęcia sprzedaży',
          type: 'date',
          required: true,
          colSpan: 6,
          tooltip: 'Potrzebna do systemu GOV oraz ułatwia wpisywanie danych. Podaj przewidywaną, jeśli jeszcze nie znasz.',
          validations: [
            { type: 'required', message: 'Data rozpoczęcia sprzedaży jest wymagana' }
          ]
        },
        {
          key: 'companyId',
          label: 'Firma',
          type: 'select',
          required: false,
          colSpan: 12,
          options: [],
          placeholder: 'Wybierz firmę (opcjonalnie)'
        },
        {
          key: 'openLocationModal',
          label: 'Adres inwestycji',
          type: 'button',
          required: true,
          colSpan: 12,
          placeholder: 'pi pi-map-marker',
          validations: [
            { type: 'required', message: 'Adres inwestycji jest wymagany' }
          ]
        }
      ]
    },
    {
      id: INVESTMENT_FORM_SECTION_IDS.CREATE_BUILDINGS,
      title: 'Budynki inwestycji',
      description: 'Budynki można dodać teraz albo później',
      icon: 'pi pi-building',
      collapsible: true,
      collapsed: true,
      fields: [
        {
          key: 'buildingsToCreate',
          label: 'Budynki',
          type: 'buildingsEditor',
          required: false,
          colSpan: 12,
          validations: [
            {
              type: 'custom',
              message: 'Uzupełnij kod dodawanego budynku.'
            }
          ]
        }
      ]
    },
    {
      id: INVESTMENT_FORM_SECTION_IDS.CREATE_ADDITIONAL,
      title: 'Notatka wewnętrzna',
      description: 'Pole opcjonalne',
      icon: 'pi-cog',
      collapsible: true,
      collapsed: false,
      fields: [
        {
          key: 'internalNote',
          label: 'Notatka wewnętrzna',
          type: 'textarea',
          required: false,
          colSpan: 12,
          rows: 3,
          placeholder: 'Dodaj notatki widoczne tylko dla zespołu...',
          tooltip: 'Ta notatka jest widoczna tylko dla Ciebie i Twoich współpracowników'
        }
      ]
    }
  ];

  return {
    id: `investment-form-${mode}`,
    title: isCreateMode ? 'Nowa Inwestycja' : 'Inwestycja',
    description: isCreateMode ? 'Utwórz nową inwestycję' : 'Edytuj inwestycję',
    icon: 'pi-building',
    mode: 'single',

    sections: isCreateMode ? getCreationSections() : getDashboardSections(),

    showValidationSummary: true,
    resetOnSubmit: false,
    confirmOnCancel: true,

    submitButtonText: undefined,
    submitButtonIcon: undefined,
    cancelButtonText: 'Anuluj',
    cancelButtonIcon: 'pi pi-times',

    onSubmit: async (data: any) => Promise.resolve(data),

    onChange: (value: any, fieldKey: string, form?: any) => {
      // Logo auto-generation removed - logo is now optional and added via onboarding
    },

    beforeSubmit: async (data: any) => {
      return data;
    },

    afterSubmit: (_response: any) => {},

    onError: (_error: any) => {}
  };
}

/**
 * Backward compatibility constant
 */
export const INVESTMENT_FORM_ENHANCED_CONFIG: FormWizardConfig<InvestmentForm> = getInvestmentFormConfig('view');


// ========================================
// HELPER FUNCTIONS
// ========================================

function parseApiDateTime(dateString: string): Date | null {
  const date = new Date(dateString);
  return Number.isNaN(date.getTime()) ? null : date;
}

/**
 * Parse API date strings to Date objects
 */
/**
 * Transform investment data FOR form (API → Form)
 * ✅ Split media object into separate logo and pictures fields
 */
export function transformInvestmentDataForForm(investment: InvestmentDetailsWithCountsDto): any {
  const formData: any = {
    title: investment.title,
    company: investment.company ? { ...investment.company } : null,
    description: investment.description || '',
    internalNote: investment.internalNote || '',
    prospectUrl: investment.prospectUrl || '',
    archived: investment.archived || false
  };

  // ✅ Extract address
  if (investment.address) {
    formData.address = { ...investment.address };
    const loc = investment.address;
    formData.locationDisplay = `${loc.street || ''}, ${loc.postalCode || ''} ${loc.city || ''}`.trim();
    formData.openLocationModal = formData.locationDisplay || 'Ustaw adres inwestycji';
  } else if ((investment as any).location) {
    // Fallback for legacy data if needed during transition
    const loc = (investment as any).location;
    if (typeof loc === 'object') {
      formData.address = { ...loc };
      formData.locationDisplay = `${loc.street || ''}, ${loc.postalCode || ''} ${loc.city || ''}`.trim();
      formData.openLocationModal = formData.locationDisplay || 'Ustaw adres inwestycji';
    }
  }

  // Media attachments are composed by the feature facade, not embedded in the investment API DTO.
  const root = investment as any;
  formData.logo = root.logo || '';
  formData.pictures = root.pictures?.length ? root.pictures : [];

  // Convert date strings to Date objects if needed
  // Prefer GOV date if available
  const saleStartDateStr = investment.gov?.saleStartDate || investment.saleStartDate;
  if (saleStartDateStr && typeof saleStartDateStr === 'string') {
    formData.saleStartDate = parseDateOnly(saleStartDateStr);
  }

  if (investment.archivedDate && typeof investment.archivedDate === 'string') {
    formData.archivedDate = parseApiDateTime(investment.archivedDate);
  }

  // ✅ Extract selling points with full address for display in kv-list
  if (investment.sellingPoints && Array.isArray(investment.sellingPoints)) {
    formData.sellingPoints = investment.sellingPoints.map(p => {
      const addressParts = [
        p.street ? `${p.street} ${p.buildingNumber || ''}`.trim() : '',
        p.city
      ].filter(Boolean);
      return {
        label: p.title,
        value: addressParts.join(', ') || 'Brak adresu'
      };
    });
  } else {
    formData.sellingPoints = [];
  }

  // ✅ Extract buildings for display in kv-list
  if (investment.buildings && Array.isArray(investment.buildings)) {
    formData.buildings = investment.buildings.map(b => {
      const addressStr = (b.addresses || [])
        .map(a => `${a.buildingNumber ?? ''}${a.entranceCode ? ' (' + a.entranceCode + ')' : ''}`)
        .join(', ');

      return {
        label: b.buildingCode,
        value: addressStr || 'Brak przypisanych adresów'
      };
    });
    formData.buildingsToCreate = investment.buildings.map((building) => ({
      buildingCode: building.buildingCode,
      addresses: (building.addresses || []).map((address) => ({
        ...(address.buildingNumber ? { buildingNumber: address.buildingNumber } : {}),
        ...(address.entranceCode ? { entranceCode: address.entranceCode } : {})
      }))
    }));
  } else {
    formData.buildings = [];
    formData.buildingsToCreate = [];
  }

  return formData;
}

export function getInvestmentFormConfigForEditPage(): FormWizardConfig<InvestmentForm> {
  return {
    id: 'investment-form-edit-page',
    title: 'Edycja inwestycji',
    description: 'Aktualizuj dane inwestycji',
    icon: 'pi-building',
    mode: 'single',
    sections: [
      {
        id: INVESTMENT_FORM_SECTION_IDS.NAME,
        title: 'Podstawowe informacje',
        description: 'Dane aktualizowane przez endpoint PUT /investments/{id}',
        icon: 'pi-info-circle',
        collapsible: false,
        fields: [
          {
            key: 'title',
            label: 'Nazwa inwestycji',
            type: 'text',
            required: true,
            colSpan: 12,
            validations: [
              { type: 'required', message: 'Nazwa inwestycji jest wymagana' },
              { type: 'minLength', value: 3, message: 'Nazwa musi mieć co najmniej 3 znaki' }
            ]
          },
          {
            key: 'description',
            label: 'Opis inwestycji',
            type: 'textarea',
            required: false,
            rows: 4,
            colSpan: 12
          },
          {
            key: 'buildingPermitNumber',
            label: 'Numer pozwolenia na budowę',
            type: 'text',
            required: false,
            colSpan: 6
          },
          {
            key: 'phase',
            label: 'Etap inwestycji',
            type: 'text',
            required: false,
            colSpan: 6
          },
          {
            key: 'internalNote',
            label: 'Notatka wewnętrzna',
            type: 'textarea',
            required: false,
            rows: 3,
            colSpan: 12
          }
        ]
      },
      {
        id: INVESTMENT_FORM_SECTION_IDS.LOCATION,
        title: 'Lokalizacja inwestycji',
        description: 'Aktualizowana dedykowanym endpointem lokalizacji',
        icon: 'pi-map-marker',
        collapsible: false,
        fields: [
          {
            key: 'openLocationModal',
            label: 'Adres inwestycji',
            type: 'button',
            required: false,
            colSpan: 12,
            buttonLabel: 'Edytuj adres inwestycji',
            placeholder: 'pi pi-map-marker'
          }
        ]
      }
    ],
    showValidationSummary: true,
    resetOnSubmit: false,
    confirmOnCancel: true,
    cancelButtonText: 'Anuluj',
    cancelButtonIcon: 'pi pi-times',
    onSubmit: async (data: any) => Promise.resolve(data),
    beforeSubmit: async (data: any) => data,
    afterSubmit: (_response: any) => { },
    onError: (_error: any) => { }
  };
}

/**
 * Get form config for editing existing investment
 * ✅ Set investmentId for media upload fields
 */
export function getInvestmentFormConfigForEdit(
  investmentData?: InvestmentDetailsWithCountsDto,
  investmentId?: number
): FormWizardConfig<InvestmentForm> {
  return getInvestmentFormConfig('edit', investmentId, investmentData);
}

/**
 * Get form config for creating new investment
 */
export function getInvestmentFormConfigForCreate(): FormWizardConfig<InvestmentForm> {
  return getInvestmentFormConfig('create');
}
